import { Injectable } from '@angular/core';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { LegendCursorService } from '@client/app/modules/charts/services/LegendCursor.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DoughnutChart } from '@client/app/modules/charts/models/charts/doughnut';

class AccessibilityDoughnutChart {
    accessibilityChecks: any[] = [];
    doughnutChartSettings: DoughnutChart = new DoughnutChart();
    summary: any;
    constructor (
        private readonly translateService: TranslateService,
        private readonly complianceService: ComplianceService,
        private readonly legendCursorService: LegendCursorService,
        private readonly pipeService: PipeService,
        summary,
        accessibilityChecks,
    ) {
        this.accessibilityChecks = accessibilityChecks;
        this.summary = summary;
    }

    /**
     * @memberOf AccessibilityDoughnutChartService
     * @description Return the options object of the accessibility doughnut
     * @return {Object}
     */
    getOptions (resultType) {
        const score = this.getPercentageFromResultType(resultType, null) / 100;
        const percentage = this.pipeService.get('monNumber')(score, '0.[00] %');

        this.doughnutChartSettings.setTooltipCallbacks({
            label: (tooltipItem: any, data: any) => {
                const pageCount = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                return (
                    this.pipeService.get('translateWithPlural')('Page', pageCount, 'Pages') +
                ' ' +
                this.pipeService.get('monNumber')(pageCount, '0,0')
                );
            },
        });
        this.doughnutChartSettings.setCenterText(
            percentage,
            this.translateService.getString('Accessibility'),
            this.translateService.getString('Compliance'),
        );
        this.doughnutChartSettings.setTotalFontSize(170);
        this.doughnutChartSettings.setSubFontSize(170);
        this.doughnutChartSettings.setCutOutPercentage(80);
        this.doughnutChartSettings.setTooltipSettings({ enabled: false });
        this.doughnutChartSettings.setHoverSettings({ mode: null });
        this.doughnutChartSettings.options = this.legendCursorService.setHoverState(this.doughnutChartSettings.options);
        return this.doughnutChartSettings.options;
    }

    /**
     * @memberOf AccessibilityDoughnutChartService
     * @description Return the labels of the accessibility doughnut
     * @return {Array}
     */
    getLabels () {
        return [this.translateService.getString('Pages'), this.translateService.getString('Pages')];
    }

    /**
     * @memberOf AccessibilityDoughnutChartService
     * @description Return the colors of the accessibility doughnut
     * @return {Array}
     */
    getColors () {
        return ['#4dc0bd', '#dcdcdc'];
    }

    /**
     * @memberOf AccessibilityDoughnutChartService
     * @param resultType
     * @param pageCountAttr
     * @returns {*}
     */
    getData (resultType, pageCountAttr) {
        return this.complianceService.accessibilityCompliance(
            resultType,
            pageCountAttr,
            this.accessibilityChecks,
            this.summary,
        );
    }

    /**
     * @memberOf AccessibilityDoughnutChartService
     * @param resultType
     * @param pageCountAttr
     * @returns {*|number}
     */
    getPercentageFromResultType (resultType, pageCountAttr) {
        return this.complianceService.accessibilityCompliancePercentageFromResultType(
            resultType,
            pageCountAttr,
            this.accessibilityChecks,
            this.summary,
        );
    }
}

@Injectable({
    providedIn: 'root',

})
export class AccessibilityDoughnutChartService {
    constructor (
        private readonly translateService: TranslateService,
        private readonly complianceService: ComplianceService,
        private readonly legendCursorService: LegendCursorService,
        private readonly pipeService: PipeService,
    ) {}

    init (summary, accessibilityChecks) {
        return new AccessibilityDoughnutChart(
            this.translateService,
            this.complianceService,
            this.legendCursorService,
            this.pipeService,
            summary,
            accessibilityChecks,
        );
    }

}
